import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminPositionLevel } from "@/modules/chart/admin/types";

export type PositionLevelList = Paginate<AdminPositionLevel<true>>;

export const positionLevels = createQueryKeys("admin-chart-position-levels", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "parent";

export const fetchPositionLevelsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...positionLevels.all(page, search),
    queryFn: () =>
      adminApi
        .get<PositionLevelList>(`/chart/positions/levels`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchPositionLevels = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchPositionLevelsQueryOptions(page, search));

export const fetchPositionLevelQueryOptions = (id?: number) =>
  queryOptions({
    ...positionLevels.one(id),
    queryFn: () =>
      adminApi
        .get<AdminPositionLevel<true>>(`/chart/positions/levels/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchPositionLevel = (id?: number) =>
  useQuery(fetchPositionLevelQueryOptions(id));

export interface PositionLevelRequest {
  name: string;
  type: "official" | "operational";
  parent_id?: number | null;
}

export const useCreatePositionLevel = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: PositionLevelRequest) =>
      adminApi
        .post("/chart/positions/levels", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: positionLevels.all._def,
      });
    },
  });
};

export const useUpdatePositionLevel = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: PositionLevelRequest) =>
      adminApi
        .put(`/chart/positions/levels/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: positionLevels.all._def,
      });
      queryClient.invalidateQueries(positionLevels.one(id));
    },
  });
};

export const useCreateOrUpdatePositionLevel = (id: number | undefined) => {
  const create = useCreatePositionLevel();
  const update = useUpdatePositionLevel(id!);
  return id ? update : create;
};

export const useDeletePositionLevel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`chart/positions/levels/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: positionLevels.all._def,
      });
    },
  });
};
